import { MoveRightIcon } from "lucide-react";
import { ExpectationComparison } from "@/components/career-path/ExpectationComparison";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { OriginLink } from "@/components/shared/origin-link";
import type { ComparisonBuckets, ComparisonItem, RoleComparison } from "@/hooks/use-career-path";
import { CategoryOverlapSummary } from "./CategoryOverlapSummary";

// Adjacent-run grouping: items arrive server-sorted by primary-category
// sortOrder (uncategorized last), so one linear pass yields the subheadings.
// A capability can carry several categories as of the category/theme merge;
// this groups by the first (its "primary" one) rather than fanning the item
// out under every category it has.
interface CategoryGroup {
  key: string;
  name: string | null;
  items: ComparisonItem[];
}

function groupByCategory(items: ComparisonItem[]): CategoryGroup[] {
  const groups: CategoryGroup[] = [];
  for (const item of items) {
    const primary = item.categories[0];
    const key = primary?.id ?? "__uncategorized__";
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({ key, name: primary?.name ?? null, items: [item] });
    }
  }
  return groups;
}

type ItemKind = "skills" | "competencies";

function ItemPills({ items, kind }: { items: ComparisonItem[]; kind: ItemKind }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <li
          key={item.id}
          className="inline-flex items-center rounded-full border bg-background px-2 py-0.5 text-xs"
        >
          {kind === "skills" ? (
            <OriginLink
              to="/dashboard/skills/$skillId"
              params={{ skillId: item.id }}
              className="text-primary hover:underline"
            >
              {item.name}
            </OriginLink>
          ) : (
            <OriginLink
              to="/dashboard/competencies/$competencyId"
              params={{ competencyId: item.id }}
              className="text-primary hover:underline"
            >
              {item.name}
            </OriginLink>
          )}
        </li>
      ))}
    </ul>
  );
}

// Bucket headings name the actual roles being compared so the direction of
// each change reads without referring back to the pickers.
function buildBuckets(
  fromName: string,
  toName: string,
): Array<{
  key: keyof ComparisonBuckets;
  label: string;
  accent: string;
  headerBg: string;
  dot: string;
  blurb: string;
}> {
  return [
    {
      key: "added",
      label: `New in ${toName}`,
      accent: "text-success-foreground",
      headerBg: "bg-success-bg",
      dot: "bg-success",
      blurb: "you'd need to gain these",
    },
    {
      key: "overlap",
      label: "Overlapping",
      accent: "text-info-foreground",
      headerBg: "bg-info-bg",
      dot: "bg-info",
      blurb: `required by both ${fromName} and ${toName}`,
    },
    {
      key: "removed",
      label: `Not required in ${toName}`,
      accent: "text-warning-foreground",
      headerBg: "bg-warning-bg",
      dot: "bg-warning",
      blurb: `required by ${fromName} today`,
    },
  ];
}

/** One kind (skills or competencies) inside a bucket, grouped by category. */
function KindColumn({
  title,
  items,
  kind,
}: {
  title: string;
  items: ComparisonItem[];
  kind: ItemKind;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title} · {items.length}
      </div>
      {items.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">None.</p>
      ) : (
        <div className="mt-1.5 space-y-2">
          {groupByCategory(items).map((group) => (
            <div key={group.key}>
              {group.name !== null && (
                <div className="mb-1 text-xs text-muted-foreground">{group.name}</div>
              )}
              <ItemPills items={group.items} kind={kind} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One comparison bucket (new / overlapping / not required) as a full-width,
 * visually distinct card: a tinted header row carrying the label, count, and
 * blurb, then the bucket's skills and competencies side by side.
 */
function BucketSection({
  meta,
  skills,
  competencies,
}: {
  meta: ReturnType<typeof buildBuckets>[number];
  skills: ComparisonItem[];
  competencies: ComparisonItem[];
}) {
  const total = skills.length + competencies.length;
  return (
    <section className="overflow-hidden rounded-lg border">
      <div className={`space-y-0.5 border-b px-4 py-2.5 ${meta.headerBg}`}>
        <div className="flex items-center gap-2">
          <span className={`size-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
          <span className={`text-xs font-bold uppercase tracking-wide ${meta.accent}`}>
            {meta.label}
          </span>
          <span className={`text-xs font-semibold tabular-nums ${meta.accent}`}>{total}</span>
        </div>
        <p className={`pl-4 text-xs ${meta.accent} opacity-80`}>{meta.blurb}</p>
      </div>
      {total === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">Nothing in this bucket.</p>
      ) : (
        <div className="grid gap-4 px-4 py-3 sm:grid-cols-2">
          <KindColumn title="Skills" items={skills} kind="skills" />
          <KindColumn title="Competencies" items={competencies} kind="competencies" />
        </div>
      )}
    </section>
  );
}

export function ComparisonResults({ comparison }: { comparison: RoleComparison }) {
  const { from, to, skills, competencies, categories, categoryCoverage } = comparison;
  // One role against itself: the requirement sets are identical by definition,
  // so the three buckets would read "everything overlaps, nothing changes" —
  // noise. Drop straight to the per-level expectations, which is the only thing
  // that actually differs between two levels of the one role.
  const sameRole = from.id === to.id;
  const nothingDefined =
    skills.overlap.length === 0 &&
    skills.added.length === 0 &&
    skills.removed.length === 0 &&
    competencies.overlap.length === 0 &&
    competencies.added.length === 0 &&
    competencies.removed.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
        <span>{from.name}</span>
        {from.status !== "active" && <LifecycleBadge status={from.status} />}
        {!sameRole && (
          <>
            <MoveRightIcon className="size-4 text-muted-foreground" />
            <span>{to.name}</span>
            {to.status !== "active" && <LifecycleBadge status={to.status} />}
          </>
        )}
        {sameRole && (
          <span className="font-normal text-muted-foreground">— comparing two career levels</span>
        )}
      </div>

      {nothingDefined ? (
        <div className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
          {sameRole
            ? `${from.name} has no required skills or competencies defined yet, so there's nothing to compare across its levels.`
            : "Neither role has required skills or competencies defined yet, so there's nothing to compare."}
        </div>
      ) : (
        <div className="space-y-4">
          {/* The category-level answer first: it is the one that says a move
              is not starting from nothing, which the per-capability buckets
              below cannot. Pointless on a role against itself. */}
          {!sameRole && (
            <CategoryOverlapSummary
              from={from}
              to={to}
              categories={categories}
              coverage={categoryCoverage}
            />
          )}

          {!sameRole &&
            buildBuckets(from.name, to.name).map((meta) => (
              <BucketSection
                key={meta.key}
                meta={meta}
                skills={skills[meta.key]}
                competencies={competencies[meta.key]}
              />
            ))}

          {/* The buckets answer *which* capabilities change; this answers what
              the bar is on either side, at a career level picked per role. */}
          <ExpectationComparison from={from} to={to} sameRole={sameRole} />
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {sameRole
          ? "Which capabilities a role requires is level-free by design — the same list applies at every level, and only the expectation against each one moves."
          : "Which capabilities a role requires is level-free by design — the career level doesn't change the list, it changes the expectation against each one."}
      </p>
    </div>
  );
}
